import { useState } from "react";
import { useDashboard } from "@/contexts/DashboardContext";
import { cn, formatPercentage } from "@/lib/utils";
import { useAgentPerQueueBreakdown } from "@/hooks/useAgentPerQueueBreakdown";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { TablePagingFooter } from "@/components/widgets/common/TablePagingFooter";
import { WidgetTitle } from "@/components/widgets/WidgetTitle";
import { formatDuration, renderMetricBar } from "./tableCellHelpers";
import {
  TableGridHeaderRow,
  TableGridHeaderCell,
  TableGridRow,
  TableGridCell,
  TableGridSkeletonRow,
  TABLE_HEADER_TITLE_CLASS,
  METRIC_BAR_TRACK_CLASS,
  type TableColumnConfig,
} from "@/components/ui/table";

function slaClass(v: number) {
  if (v >= 80) return { text: "text-success", fill: "hsl(var(--success))" };
  if (v >= 70) return { text: "text-caution", fill: "hsl(var(--caution))" };
  return { text: "text-destructive", fill: "hsl(var(--destructive))" };
}

const COLUMNS = [
  { key: "name", label: "Queue / Direct line", firstCol: true },
  { key: "offered", label: "Offered" },
  { key: "handled", label: "Handled" },
  { key: "missed", label: "Missed" },
  { key: "transferred", label: "Transferred" },
  { key: "sla", label: "SLA" },
  { key: "ahtSec", label: "AHT" },
] as const;

const SKELETON_COLUMNS: TableColumnConfig[] = COLUMNS.map((col) => ({
  title: col.label,
  key: col.key,
  width: 120,
}));
// Old loading state was a plain `h-[120px]` text block — keep that same height,
// clip skeleton rows (fixed 45px each, unchanged) to fit inside it.
const SKELETON_CONTAINER_HEIGHT = 120;
const SKELETON_ROW_HEIGHT = 45;
const SKELETON_ROW_COUNT = Math.ceil(SKELETON_CONTAINER_HEIGHT / SKELETON_ROW_HEIGHT);

interface Props {
  readonly agentId: string | null;
  readonly startDate: string | undefined;
  readonly endDate: string | undefined;
  readonly queueMicrosoftIds?: string[] | null;
}

export function UserAgentPerQueueBreakdownTable({ agentId, startDate, endDate, queueMicrosoftIds }: Props) {
  const { queues, isLoading } = useAgentPerQueueBreakdown(agentId, startDate, endDate, queueMicrosoftIds);
  const { theme } = useDashboard();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const totalPages = Math.max(1, Math.ceil(queues.length / pageSize));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const pageStart = (pageSafe - 1) * pageSize;
  const pageSlice = queues.slice(pageStart, pageStart + pageSize);

  const barColor = theme === "dark" ? "bg-info" : "bg-action";
  const maxOf = (key: "offered" | "handled" | "missed" | "transferred" | "ahtSec") =>
    Math.max(1, ...queues.map((q) => q[key]));
  const maxOffered = maxOf("offered");
  const maxHandled = maxOf("handled");
  const maxMissed = maxOf("missed");
  const maxTransferred = maxOf("transferred");
  const maxAht = maxOf("ahtSec");

  return (
    // eslint-disable-next-line no-restricted-syntax -- Card container border/bg, not table row/header coloring.
    <Card className="w-full flex flex-col overflow-hidden dark:bg-card dark:text-white dark:border-primary-fixed rounded-lg">
      <CardHeader className="flex flex-row items-center justify-between rounded-t-lg flex-shrink-0">
        <WidgetTitle>Per-queue breakdown</WidgetTitle>
      </CardHeader>
      <CardContent className="p-0 flex flex-col min-h-0 overflow-hidden">
        {(() => {
          if (isLoading) {
            return (
              <div className="w-full overflow-hidden" style={{ height: SKELETON_CONTAINER_HEIGHT }}>
                <div className="table w-full call-journey-table" style={{ borderCollapse: "collapse", borderSpacing: 0 }}>
                  <div className="table-row-group">
                    {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
                      <TableGridSkeletonRow key={i} columns={SKELETON_COLUMNS} />
                    ))}
                  </div>
                </div>
              </div>
            );
          }
          return (
            <>
              <div className="w-full overflow-x-auto">
                <div className="table w-full call-journey-table" style={{ borderCollapse: "collapse", borderSpacing: 0 }}>
                  {/* Header Row */}
                  <TableGridHeaderRow className={cn("text-xs border-t", TABLE_HEADER_TITLE_CLASS, "z-50")}>
                    {COLUMNS.map((col) => (
                      <TableGridHeaderCell
                        key={col.key}
                        className={cn(
                          "border-b dark:border-t border-l-0 border-r-0 border-gray-300 dark:border-0 text-left",
                          "firstCol" in col && col.firstCol ? "pl-3 pr-2 sm:pl-6 sm:pr-4" : "px-2 sm:px-4"
                        )}
                      >
                        {col.label}
                      </TableGridHeaderCell>
                    ))}
                  </TableGridHeaderRow>

                  {/* Data Rows */}
                  {pageSlice.length > 0 && (
                    <div className="table-row-group border-t border-gray-300 dark:border-0">
                      {pageSlice.map((q, idx) => {
                        const missedPct = q.missed > 0 ? Math.min((q.missed / maxMissed) * 100, 100) : 0;
                        const { text: slaText, fill: slaFill } = slaClass(q.sla ?? 0);
                        const cellBorder = "border-b";
                        return (
                          <TableGridRow key={`${q.name}-${idx}`} index={idx} className={cellBorder}>
                            <TableGridCell index={idx} className={cn("h-[45px] pl-3 pr-2 sm:pl-6 sm:pr-4 text-xs text-primary dark:text-white border-gray-300 dark:border-0 transition-colors text-left", cellBorder)}>
                              <div className="font-medium">{q.name}</div>
                              {q.line && <div className="font-mono text-[11px] text-muted-foreground">{q.line}</div>}
                            </TableGridCell>
                            <TableGridCell index={idx} className={cn("h-[45px] px-2 sm:px-4 text-xs text-primary dark:text-white border-gray-300 dark:border-0 transition-colors text-left", cellBorder)}>
                              {renderMetricBar(q.offered, Math.min((q.offered / maxOffered) * 100, 100), barColor, "", String(q.offered))}
                            </TableGridCell>
                            <TableGridCell index={idx} className={cn("h-[45px] px-2 sm:px-4 text-xs text-primary dark:text-white border-gray-300 dark:border-0 transition-colors text-left", cellBorder)}>
                              {renderMetricBar(q.handled, Math.min((q.handled / maxHandled) * 100, 100), barColor, "", String(q.handled))}
                            </TableGridCell>
                            <TableGridCell index={idx} className={cn("h-[45px] px-2 sm:px-4 text-xs text-primary dark:text-white border-gray-300 dark:border-0 transition-colors text-left", cellBorder)}>
                              {renderMetricBar(q.missed, missedPct, "bg-destructive", q.missed > 0 ? "text-destructive" : "", String(q.missed))}
                            </TableGridCell>
                            <TableGridCell index={idx} className={cn("h-[45px] px-2 sm:px-4 text-xs text-primary dark:text-white border-gray-300 dark:border-0 transition-colors text-left", cellBorder)}>
                              {renderMetricBar(q.transferred, Math.min((q.transferred / maxTransferred) * 100, 100), barColor, "", String(q.transferred))}
                            </TableGridCell>
                            <TableGridCell index={idx} className={cn("h-[45px] px-2 sm:px-4 text-xs text-primary dark:text-white border-gray-300 dark:border-0 transition-colors text-left", cellBorder)}>
                              {q.sla == null ? (
                                <span className="shrink-0">—</span>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <div className={cn("w-[50px] shrink-0", METRIC_BAR_TRACK_CLASS)}>
                                    {q.sla > 0 && <div className="h-full" style={{ width: `${q.sla}%`, background: slaFill }} />}
                                  </div>
                                  <span className={cn("text-xs", slaText)}>{formatPercentage(q.sla)}</span>
                                </div>
                              )}
                            </TableGridCell>
                            <TableGridCell index={idx} className={cn("h-[45px] px-2 sm:px-4 text-xs text-primary dark:text-white border-gray-300 dark:border-0 transition-colors text-left", cellBorder)}>
                              {renderMetricBar(q.ahtSec, Math.min((q.ahtSec / maxAht) * 100, 100), barColor, "", formatDuration(q.ahtSec))}
                            </TableGridCell>
                          </TableGridRow>
                        );
                      })}
                    </div>
                  )}
                </div>
                {pageSlice.length === 0 && (
                  <div className="flex items-center justify-center py-12 text-sm text-muted-foreground dark:text-white border-b border-gray-300 dark:border-0">
                    No call records were found
                  </div>
                )}
              </div>
              <TablePagingFooter
                currentPage={pageSafe}
                totalPages={totalPages}
                total={queues.length}
                pageSize={pageSize}
                onPageChange={setPage}
                pageSizeOptions={[5, 10, 20, 50]}
                onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                bordered={theme === "dark"}
              />
            </>
          );
        })()}
      </CardContent>
    </Card>
  );
}
